/**
 * Responsible-contact prompt banner (OCP-524).
 *
 * Accounts created before the Terms clause landed carry no responsible contact.
 * They keep working exactly as before - the gate only blocks concluding a NEW
 * contract - but they must be told, otherwise the first sign they get is a
 * blocked checkout. This banner is that prompt: persistent, non-blocking, and
 * it disappears the moment a contact is named.
 *
 * Deliberately quiet compared to the email-verification banner (which blocks
 * real functionality): informational blue rather than warning amber, and it
 * links to Settings instead of carrying its own form, so the customer lands on
 * the surface that also shows the change history.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import {
  RESPONSIBLE_CONTACT_PATH,
  type ResponsibleContactResponse,
} from './ResponsibleContactGate';

export function ResponsibleContactBanner() {
  const { t } = useTranslation('responsible-contact');
  const { user } = useAuth();
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    // Unverified accounts already carry the verification banner and cannot buy
    // anything yet - do not stack a second banner on them.
    if (!user || !user.emailVerified) {
      setMissing(false);
      return;
    }
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<ResponsibleContactResponse>(RESPONSIBLE_CONTACT_PATH, {
          signal: ac.signal,
        });
        setMissing(r.contact === null);
      } catch {
        // Never let a probe failure paint a banner that might be wrong.
        setMissing(false);
      }
    })();
    return () => ac.abort();
  }, [user]);

  if (!missing) return null;

  return (
    <div
      className="border-b border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-900/30 px-6 py-2 text-sm text-sky-900 dark:text-sky-200"
      data-testid="responsible-contact-banner"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
        <span>{t('banner.text')}</span>
        <Link
          to="/settings?tab=contact"
          className="shrink-0 rounded border border-sky-300 dark:border-sky-800 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-sky-800 dark:text-sky-200 hover:bg-sky-100 dark:hover:bg-sky-900/40"
        >
          {t('banner.cta')}
        </Link>
      </div>
    </div>
  );
}
